import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { GridModule, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  SearchCriteria,
  SearchResults,
  SearchResultItem,
  SearchEntityType,
  LifecycleState
} from '../../models/file-vault.models';

export interface SearchResultAction {
  action: 'view' | 'select';
  item: SearchResultItem;
}

@Component({
  selector: 'fv-search-results',
  standalone: true,
  template: `
    <div class="search-results-container">
      <!-- Header with tabs and sort -->
      <div class="results-header">
        <div class="tabs-container">
          <button class="tab"
                  [class.active]="activeTab === SearchEntityType.All"
                  (click)="setActiveTab(SearchEntityType.All)">
            All
            <span class="tab-count" *ngIf="totalCount > 0">{{ totalCount }}</span>
          </button>
          <button class="tab"
                  [class.active]="activeTab === SearchEntityType.Part"
                  (click)="setActiveTab(SearchEntityType.Part)"
                  *ngIf="facets['Part'] > 0 || activeTab === SearchEntityType.Part">
            Parts
            <span class="tab-count">{{ facets['Part'] || 0 }}</span>
          </button>
          <button class="tab"
                  [class.active]="activeTab === SearchEntityType.Document"
                  (click)="setActiveTab(SearchEntityType.Document)"
                  *ngIf="facets['Document'] > 0 || activeTab === SearchEntityType.Document">
            Documents
            <span class="tab-count">{{ facets['Document'] || 0 }}</span>
          </button>
          <button class="tab"
                  [class.active]="activeTab === SearchEntityType.File"
                  (click)="setActiveTab(SearchEntityType.File)"
                  *ngIf="facets['File'] > 0 || activeTab === SearchEntityType.File">
            Files
            <span class="tab-count">{{ facets['File'] || 0 }}</span>
          </button>
          <button class="tab"
                  [class.active]="activeTab === SearchEntityType.Change"
                  (click)="setActiveTab(SearchEntityType.Change)"
                  *ngIf="facets['Change'] > 0 || activeTab === SearchEntityType.Change">
            Changes
            <span class="tab-count">{{ facets['Change'] || 0 }}</span>
          </button>
        </div>

        <div class="sort-container" *ngIf="gridData.total > 0">
          <label>Sort by:</label>
          <kendo-dropdownlist
            [(ngModel)]="sortBy"
            [data]="sortOptions"
            textField="text"
            valueField="value"
            [valuePrimitive]="true"
            (valueChange)="onSortChange($event)"
            class="sort-dropdown">
          </kendo-dropdownlist>
        </div>
      </div>

      <!-- Search info -->
      <div class="search-info" *ngIf="searchQuery && !isLoading">
        <span class="result-count">{{ gridData.total }} results for "<strong>{{ searchQuery }}</strong>"</span>
        <span class="execution-time" *ngIf="executionTimeMs > 0">({{ executionTimeMs }}ms)</span>
      </div>

      <!-- Loading -->
      <div class="loading" *ngIf="isLoading">
        <div class="loading-spinner"></div>
        Searching...
      </div>

      <!-- Results Grid -->
      <kendo-grid
        *ngIf="!isLoading && gridData.total > 0"
        [data]="gridData"
        [pageSize]="pageSize"
        [skip]="skip"
        [pageable]="{ buttonCount: 5, info: true, type: 'numeric', pageSizes: [10, 20, 50] }"
        [sortable]="true"
        [sort]="sort"
        (pageChange)="onPageChange($event)"
        (sortChange)="onGridSortChange($event)"
        [selectable]="{ mode: 'single' }"
        (selectionChange)="onSelectionChange($event)"
        class="results-grid">

        <kendo-grid-column field="entityType" title="Type" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="type-badge" [ngClass]="getTypeBadgeClass(dataItem.entityType)">
              {{ dataItem.entityType }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="identifier" title="ID" [width]="140">
          <ng-template kendoGridCellTemplate let-dataItem>
            <a class="identifier-link" (click)="onViewClick(dataItem)">{{ dataItem.identifier }}</a>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="name" title="Name" [width]="250">
          <ng-template kendoGridCellTemplate let-dataItem>
            <div class="name-cell">
              <span class="name-text" [title]="dataItem.name">{{ dataItem.name }}</span>
              <span class="highlight-text" *ngIf="dataItem.highlight" [innerHTML]="dataItem.highlight"></span>
            </div>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="revision" title="Rev" [width]="60" *ngIf="showRevision">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.revision || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="state" title="State" [width]="100" *ngIf="showState">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="state-badge" [ngClass]="getStateBadgeClass(dataItem.state)" *ngIf="dataItem.state">
              {{ dataItem.state }}
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="subType" title="Sub Type" [width]="100" *ngIf="showSubType">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="subtype-text">{{ dataItem.subType || '-' }}</span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="createdBy" title="Created By" [width]="120">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.createdBy || '-' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="modifiedAt" title="Modified" [width]="100">
          <ng-template kendoGridCellTemplate let-dataItem>
            {{ dataItem.modifiedAt | date:'shortDate' }}
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column field="score" title="Score" [width]="70" *ngIf="showScore">
          <ng-template kendoGridCellTemplate let-dataItem>
            <span class="score-indicator" [style.opacity]="getScoreOpacity(dataItem.score)">
              {{ (dataItem.score * 100).toFixed(0) }}%
            </span>
          </ng-template>
        </kendo-grid-column>

        <kendo-grid-column title="" [width]="80">
          <ng-template kendoGridCellTemplate let-dataItem>
            <button kendoButton [look]="'flat'" (click)="onViewClick(dataItem)">View</button>
          </ng-template>
        </kendo-grid-column>
      </kendo-grid>

      <!-- Empty state -->
      <div class="empty-state" *ngIf="!isLoading && gridData.total === 0 && searchQuery">
        <div class="empty-icon">🔍</div>
        <p class="empty-title">No results found</p>
        <p class="empty-message">
          No matches for "<strong>{{ searchQuery }}</strong>"
          <span *ngIf="activeTab !== SearchEntityType.All">in {{ getEntityTypeLabel(activeTab) }}</span>
        </p>
        <div class="empty-suggestions">
          <p>Try:</p>
          <ul>
            <li>Checking your spelling</li>
            <li>Using more general terms</li>
            <li>Searching in "All" categories</li>
          </ul>
        </div>
      </div>

      <!-- Initial state -->
      <div class="initial-state" *ngIf="!isLoading && !searchQuery">
        <div class="initial-icon">🔍</div>
        <p>Enter a search term to find parts, documents, files, and changes</p>
      </div>
    </div>
  `,
  styles: [`
    .search-results-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .results-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      background: var(--fv-surface-hover);
      border-bottom: 1px solid var(--fv-border);
      flex-wrap: wrap;
      gap: 8px;
    }

    .tabs-container {
      display: flex;
      gap: 4px;
    }

    .tab {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 8px 16px;
      border: none;
      background: transparent;
      color: var(--fv-text-secondary);
      font-size: 13px;
      font-weight: 500;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.2s;
    }

    .tab:hover {
      background: var(--fv-surface-hover);
    }

    .tab.active {
      background: var(--fv-text-accent);
      color: white;
    }

    .tab-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 20px;
      height: 20px;
      padding: 0 6px;
      font-size: 11px;
      font-weight: 600;
      background: var(--fv-shadow);
      border-radius: 10px;
    }

    .tab.active .tab-count {
      background: rgba(255,255,255,0.2);
    }

    .sort-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .sort-container label {
      font-size: 13px;
      color: var(--fv-text-secondary);
    }

    .sort-dropdown {
      width: 150px;
    }

    .search-info {
      padding: 8px 12px;
      font-size: 13px;
      color: var(--fv-text-secondary);
      background: var(--fv-surface-alt);
      border-bottom: 1px solid var(--fv-border-subtle);
    }

    .result-count strong {
      color: var(--fv-text);
    }

    .execution-time {
      color: var(--fv-text-muted);
      font-size: 12px;
      margin-left: 8px;
    }

    .loading {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 12px;
      padding: 48px;
      color: var(--fv-text-secondary);
    }

    .loading-spinner {
      width: 24px;
      height: 24px;
      border: 3px solid var(--fv-border);
      border-top-color: var(--fv-text-accent);
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      to { transform: rotate(360deg); }
    }

    .results-grid {
      flex: 1;
      font-size: 13px;
    }

    .type-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 600;
      text-transform: uppercase;
    }

    .type-badge.part { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .type-badge.document { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .type-badge.file { background: var(--fv-status-purple-bg); color: var(--fv-status-purple-fg); }
    .type-badge.change { background: var(--fv-status-error-bg); color: var(--fv-status-error-fg); }

    .identifier-link {
      color: var(--fv-text-accent);
      cursor: pointer;
      font-family: monospace;
      font-weight: 500;
    }

    .identifier-link:hover {
      text-decoration: underline;
    }

    .name-cell {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .name-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .highlight-text {
      font-size: 11px;
      color: var(--fv-text-secondary);
      background: var(--fv-status-warning-bg);
      padding: 2px 4px;
      border-radius: 2px;
    }

    .highlight-text em, .highlight-text mark {
      background: #ffeb3b;
      font-style: normal;
      font-weight: 600;
    }

    .state-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 500;
    }

    .state-badge.inwork { background: var(--fv-status-warning-bg); color: var(--fv-status-warning-fg); }
    .state-badge.inreview { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.approved { background: var(--fv-status-success-bg); color: var(--fv-status-success-fg); }
    .state-badge.released { background: var(--fv-status-info-bg); color: var(--fv-status-info-fg); }
    .state-badge.superseded { background: var(--fv-status-bluegrey-bg); color: var(--fv-status-bluegrey-fg); }
    .state-badge.obsolete { background: var(--fv-status-draft-bg); color: var(--fv-status-draft-fg); }

    .subtype-text {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .score-indicator {
      display: inline-block;
      padding: 2px 6px;
      background: #4caf50;
      color: white;
      font-size: 10px;
      font-weight: 600;
      border-radius: 3px;
    }

    .empty-state, .initial-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 48px;
      text-align: center;
      color: var(--fv-text-secondary);
    }

    .empty-icon, .initial-icon {
      font-size: 48px;
      margin-bottom: 16px;
      opacity: 0.5;
    }

    .empty-title {
      font-size: 18px;
      font-weight: 500;
      color: var(--fv-text);
      margin-bottom: 8px;
    }

    .empty-message {
      margin-bottom: 16px;
    }

    .empty-suggestions {
      text-align: left;
      background: var(--fv-surface-hover);
      padding: 16px 24px;
      border-radius: 8px;
    }

    .empty-suggestions p {
      margin-bottom: 8px;
      font-weight: 500;
    }

    .empty-suggestions ul {
      margin: 0;
      padding-left: 20px;
    }

    .empty-suggestions li {
      margin: 4px 0;
    }
  `],
  imports: [CommonModule, FormsModule, GridModule, ButtonsModule, DropDownsModule]
})
export class SearchResultsComponent implements OnInit, OnChanges {
  private api = inject(FileVaultApiService);

  // Expose enum to template
  SearchEntityType = SearchEntityType;

  /** Search query to display */
  @Input() searchQuery = '';

  /** Search criteria - when provided, component will execute search */
  @Input() criteria?: SearchCriteria;

  /** Initial entity type tab */
  @Input() initialEntityType: SearchEntityType = SearchEntityType.All;

  /** Show revision column */
  @Input() showRevision = true;

  /** Show state column */
  @Input() showState = true;

  /** Show sub type column */
  @Input() showSubType = true;

  /** Show relevance score column */
  @Input() showScore = false;

  /** Page size */
  @Input() pageSize = 20;

  /** Emitted when user clicks view or selects a result */
  @Output() resultAction = new EventEmitter<SearchResultAction>();

  /** Emitted when tab changes */
  @Output() entityTypeChange = new EventEmitter<SearchEntityType>();

  gridData: GridDataResult = { data: [], total: 0 };
  isLoading = false;
  skip = 0;
  sort: SortDescriptor[] = [{ field: 'score', dir: 'desc' }];
  sortBy = 'relevance';
  activeTab: SearchEntityType = SearchEntityType.All;
  totalCount = 0;
  executionTimeMs = 0;

  facets: Record<string, number> = {
    Part: 0,
    Document: 0,
    File: 0,
    Change: 0
  };

  sortOptions = [
    { text: 'Relevance', value: 'relevance' },
    { text: 'Name (A-Z)', value: 'name-asc' },
    { text: 'Name (Z-A)', value: 'name-desc' },
    { text: 'Newest', value: 'date-desc' },
    { text: 'Oldest', value: 'date-asc' }
  ];

  ngOnInit(): void {
    this.activeTab = this.initialEntityType;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['criteria'] && this.criteria) {
      this.searchQuery = this.criteria.query;
      if (this.criteria.entityType !== undefined) {
        this.activeTab = this.criteria.entityType;
      }
      this.skip = 0;
      this.performSearch();
    }
  }

  setActiveTab(entityType: SearchEntityType): void {
    if (this.activeTab !== entityType) {
      this.activeTab = entityType;
      this.skip = 0;
      this.entityTypeChange.emit(entityType);
      if (this.searchQuery) {
        this.performSearch();
      }
    }
  }

  async performSearch(): Promise<void> {
    if (!this.searchQuery?.trim()) {
      this.gridData = { data: [], total: 0 };
      return;
    }

    this.isLoading = true;

    try {
      const searchCriteria: SearchCriteria = {
        query: this.searchQuery,
        entityType: this.activeTab === SearchEntityType.All ? undefined : this.activeTab,
        page: Math.floor(this.skip / this.pageSize) + 1,
        pageSize: this.pageSize,
        sortBy: this.getSortField(),
        sortDescending: this.getSortDirection(),
        ...this.criteria
      };

      const result = await this.api.search(searchCriteria).toPromise();

      if (result) {
        this.gridData = {
          data: result.items,
          total: result.totalCount
        };
        this.totalCount = result.totalCount;
        this.executionTimeMs = result.executionTimeMs;
        this.updateFacets(result);
      }
    } catch (error) {
      console.error('Search failed', error);
      this.gridData = { data: [], total: 0 };
    } finally {
      this.isLoading = false;
    }
  }

  private updateFacets(result: SearchResults): void {
    if (result.entityTypeFacets) {
      this.facets = {
        Part: result.entityTypeFacets['Part'] || 0,
        Document: result.entityTypeFacets['Document'] || 0,
        File: result.entityTypeFacets['File'] || 0,
        Change: result.entityTypeFacets['Change'] || 0
      };
    }
  }

  private getSortField(): string {
    switch (this.sortBy) {
      case 'name-asc':
      case 'name-desc':
        return 'name';
      case 'date-asc':
      case 'date-desc':
        return 'modifiedAt';
      default:
        return 'score';
    }
  }

  private getSortDirection(): boolean {
    return this.sortBy.endsWith('-desc') || this.sortBy === 'relevance';
  }

  onSortChange(value: string): void {
    this.sortBy = value;
    this.skip = 0;
    this.performSearch();
  }

  onPageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take;
    this.performSearch();
  }

  onGridSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    if (sort.length > 0) {
      const field = sort[0].field;
      const dir = sort[0].dir;
      if (field === 'name') {
        this.sortBy = dir === 'desc' ? 'name-desc' : 'name-asc';
      } else if (field === 'modifiedAt') {
        this.sortBy = dir === 'desc' ? 'date-desc' : 'date-asc';
      } else {
        this.sortBy = 'relevance';
      }
    }
    this.performSearch();
  }

  onSelectionChange(event: any): void {
    const selected = event.selectedRows?.[0]?.dataItem;
    if (selected) {
      this.resultAction.emit({ action: 'select', item: selected });
    }
  }

  onViewClick(item: SearchResultItem): void {
    this.resultAction.emit({ action: 'view', item });
  }

  getTypeBadgeClass(entityType: string): string {
    return entityType?.toLowerCase() || '';
  }

  getStateBadgeClass(state?: string): string {
    if (!state) return '';
    return state.toLowerCase().replace(/\s+/g, '');
  }

  getScoreOpacity(score: number): number {
    return Math.max(0.4, score);
  }

  getEntityTypeLabel(entityType: SearchEntityType): string {
    switch (entityType) {
      case SearchEntityType.Part: return 'Parts';
      case SearchEntityType.Document: return 'Documents';
      case SearchEntityType.File: return 'Files';
      case SearchEntityType.Change: return 'Changes';
      default: return 'All';
    }
  }

  /** Public method to refresh search results */
  refresh(): void {
    this.performSearch();
  }

  /** Public method to clear results */
  clear(): void {
    this.searchQuery = '';
    this.gridData = { data: [], total: 0 };
    this.totalCount = 0;
    this.facets = { Part: 0, Document: 0, File: 0, Change: 0 };
  }
}
